/* eslint-disable camelcase */
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { parse as parseQuery } from 'qs';
import dayjs from 'dayjs';

import { PortalTransactions, useScreenQuery } from 'playup-web-core';

import { categoryIds } from '../../constants/transactions';
import Pagination from '../../../components/Pagination';
import paginationBuilder from '../../../utils/paginationBuilder';
import PortalDatePicker from '../../../components/DatePicker/PortalDatePicker';
import { exportTransactions, getTransactions } from '../../../api/wallets';
import appConfig from '../../../appConfig';

const Transactions = () => {
	const [dateRange, setDateRange] = React.useState<{
		from: Date;
		to: Date;
	}>({
		from: dayjs().subtract(1, 'year').add(1, 'day').toDate(),
		to: new Date(),
	});
	const [transactions, setTransactions] = React.useState<Array<WalletTransaction>>([]);
	const [exportError, setExportError] = React.useState<string | undefined>();
	const [category, setCategory] = React.useState<'all' | 'deposits' | 'withdrawals' | 'wins'>('all');
	const [loading, setLoading] = React.useState<boolean>(true);
	const [pagination, setPagination] = React.useState<Array<PaginationLink>>([]);
	const { isSmall } = useScreenQuery();

	const location = useLocation();
	const history = useHistory();
	const { page = '1' } = parseQuery(location.search, {
		ignoreQueryPrefix: true,
	});

	const loadTransactions = () => {
		setLoading(true);

		const transactionsParams: {
			category_ids?: number;
			page: string;
			transaction_from?: string;
			transaction_to?: string;
		} = {
			category_ids: categoryIds[category],
			page: page as string,
		};

		if (!!dateRange.from && !!dateRange.to) {
			transactionsParams.transaction_from = dayjs(dateRange.from).format('YYYY-MM-DDT[00:00:00]Z');
			transactionsParams.transaction_to = dayjs(dateRange.to).add(1, 'day').format('YYYY-MM-DDT[00:00:00]Z');
		}

		getTransactions(transactionsParams)
			.then((response) => {
				setTransactions(response.data);
				setPagination(paginationBuilder(response.meta.pagination));
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const downloadTransactions = () => {
		setExportError(undefined);
		if (!!dateRange.from && !!dateRange.to) {
			exportTransactions({
				transaction_from: dayjs(dateRange.from).format('YYYY-MM-DDT[00:00:00]Z'),
				transaction_to: dayjs(dateRange.to).add(1, 'day').format('YYYY-MM-DDT[00:00:00]Z'),
			})
				.then((transactions) => {
					const url = window.URL.createObjectURL(transactions);
					const link = document.createElement('a');
					link.href = url;
					link.setAttribute('download', 'transaction history.csv');
					document.body.appendChild(link);
					link.click();
				})
				.catch((error) => {
					if (error.transaction_to) setExportError('Please make sure the exported date range is under one year');
					else setExportError('There was an error exporting the transactions, please try reducing the date range');
				});
		}
	};

	React.useEffect(loadTransactions, [page, category, dateRange]);

	const updateRange = (type: 'from' | 'to') => (date: Date) => {
		if (page) {
			history.replace(location.pathname);
		}

		setDateRange({
			...dateRange,
			[type]: date,
		});
	};

	return (
		<>
			<Helmet title="Cash Transactions" />
			<PortalTransactions
				isLoading={loading}
				category={category}
				region={appConfig.regions.language}
				transactions={transactions}
				dateRange={dateRange}
				exportError={exportError}
				setCategory={setCategory}
				updateRange={updateRange}
				downloadTransactions={downloadTransactions}
				DatePicker={PortalDatePicker}
				brandName={appConfig.brand.name}
			/>
			{transactions.length > 0 && <Pagination links={pagination} smallScreen={isSmall} />}
		</>
	);
};

export default Transactions;
